import { DecimalPipe } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OnyxIconName } from '@onyx/angular';
import {
  OnyxAmountPipe,
  OnyxDataWidgetComponent,
  OnyxDatePipe,
  OnyxIconComponent,
  OnyxInformationHeadingComponent,
  OnyxInformationListComponent,
  OnyxInformationRowComponent,
  OnyxLanguagePipe,
  OnyxNamePipe,
  OnyxTemperaturePipe,
} from '@onyx/angular';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import { CommonHelper } from '../../../../../common/helpers/common.helper';
import { JoinPipe } from '../../../../../common/pipes/join.pipe';
import { OrderSemiTrailerSize } from '../../../common/enums/order-semi-trailer-size';
import type { Order } from '../../../common/interfaces/order';

@Component({
  selector: 'app-order-modal-details',
  imports: [
    OnyxInformationRowComponent,
    TranslatePipe,
    OnyxIconComponent,
    OnyxTemperaturePipe,
    JoinPipe,
    OnyxDatePipe,
    OnyxDataWidgetComponent,
    OnyxNamePipe,
    OnyxLanguagePipe,
    OnyxAmountPipe,
    DecimalPipe,
    OnyxInformationListComponent,
    OnyxInformationHeadingComponent,
  ],
  templateUrl: './order-modal-details.component.html',
  styleUrl: './order-modal-details.component.scss',
})
export class OrderModalDetailsComponent {
  private readonly translateService = inject(TranslateService);

  public readonly order = input.required<Order>();

  protected readonly DictionaryCode = DictionaryCode;

  protected semiTrailerSizeIcon = computed<OnyxIconName>(() => {
    const trailerSize = this.order().basicInformation.minSemiTrailerSize;
    if (trailerSize === OrderSemiTrailerSize.AUTO) return 'refresh-outlined';

    return `semi-trailer-${trailerSize}-outlined`;
  });

  protected parameters = computed(() => {
    const parameters = CommonHelper.getSortedParameters(
      this.order().parameters,
      this.translateService,
    );

    const driverRequirementsKeys = ['driverLanguages', 'hasSanitaryInspection'];
    const otherRequirementsKeys = [
      'hasSafeParking',
      'isDedicated',
      'hasEcmr',
      'isExclusive',
      'sentNotificationNumber',
      'hasPalletExchange',
    ];

    const orderRequirements = parameters.reduce(
      (result, parameter) => {
        const { key } = parameter;

        if (driverRequirementsKeys.includes(key)) {
          result.driverRequirements.push(parameter);
        } else if (otherRequirementsKeys.includes(key)) {
          result.otherRequirements.push(parameter);
        } else {
          result.vehicleRequirements.push(parameter);
        }

        return result;
      },
      {
        driverRequirements: [] as typeof parameters,
        otherRequirements: [] as typeof parameters,
        vehicleRequirements: [] as typeof parameters,
      },
    );

    return {
      ...orderRequirements,
      vehicleRequirements: [
        ...orderRequirements.vehicleRequirements,
        {
          key: 'minSemiTrailerSize',
          name: 'labels.minSemiTrailerSize',
          value: this.order().basicInformation.minSemiTrailerSize,
        },
        {
          key: 'cargoSpace',
          name: 'labels.cargoSpace',
          value: this.order().basicInformation.vehicleTypes,
        },
        {
          key: 'dischargeTypes',
          name: 'labels.dischargeType',
          value: this.order().basicInformation.dischargeTypes,
        },
      ],
    };
  });
}
